import React, { useCallback } from 'react'
import { Box, Typography, Alert, Stack, Chip, Divider, Breadcrumbs } from '@mui/material'
import { SelectedItem } from './InspectorLayout'
import MainSegmentInspector from './inspectors/MainSegmentInspector'
import LoopInspector from './inspectors/LoopInspector'
import SegmentInspector from './inspectors/SegmentInspector'
import ElementInspector from './inspectors/ElementInspector'
import { ArrowRight } from '@mui/icons-material'
import { MAIN_SEGMENT_TITLES } from '../constants'
import { getIcon } from './NavigationItem'

export interface InspectorPaneProps {
  selectedItem: SelectedItem | null
  data: any
  onChange: (data: any) => void
  onNavigateToItem?: (item: SelectedItem) => void
  onToggleNavigationPane?: () => void
  navigationPaneVisible?: boolean
  readonly?: boolean
}

const InspectorPane = React.memo<InspectorPaneProps>(
  ({
    selectedItem,
    data,
    onChange,
    onNavigateToItem,
    onToggleNavigationPane,
    navigationPaneVisible = true,
    readonly = false,
  }) => {
    // Function to merge updated data back into the complete data structure
    const mergeUpdatedData = useCallback(
      (updatedItemData: any) => {
        if (!selectedItem) return data

        const updatedData = { ...data }
        const path = selectedItem.path

        // Debug logging
        console.log('Merging updated data:', {
          path,
          selectedItemType: selectedItem.type,
          updatedItemData,
          originalData: data,
        })

        // Navigate to the correct location in the data structure and update it
        let current = updatedData
        let i = 0

        console.log('Starting navigation with path:', path)

        while (i < path.length - 1) {
          const segment = path[i]
          console.log(`Step ${i}: Processing segment "${segment}", current:`, current)

          if (segment === 'loops') {
            // Navigate to the loops array
            if (!current.loops) {
              current.loops = []
            }
            current = current.loops
            console.log(`Navigated to loops array:`, current)
            i++
          } else if (segment === 'segments') {
            // Navigate to the segments array
            if (!current.segments) {
              current.segments = []
            }
            current = current.segments
            console.log(`Navigated to segments array:`, current)
            i++
          } else if (segment === 'elements') {
            // Navigate to the elements array
            if (!current.elements) {
              current.elements = []
            }
            current = current.elements
            console.log(`Navigated to elements array:`, current)
            i++
          } else if (!isNaN(parseInt(segment))) {
            // This is an index, navigate to the array item
            const index = parseInt(segment)
            if (!current[index]) {
              current[index] = {}
            }
            current = current[index]
            console.log(`Navigated to index ${index}:`, current)
            i++
          } else {
            // This is a property key, navigate to the object property
            if (!current[segment]) {
              current[segment] = {}
            }
            current = current[segment]
            console.log(`Navigated to property "${segment}":`, current)
            i++
          }
        }

        // Update the final item with the new data
        const lastSegment = path[path.length - 1]
        console.log(`Final step: Updating "${lastSegment}" with:`, updatedItemData)

        if (!isNaN(parseInt(lastSegment))) {
          // Last segment is an index
          const index = parseInt(lastSegment)
          current[index] = updatedItemData
          console.log(`Updated index ${index} with:`, updatedItemData)
        } else {
          // Last segment is a property key
          current[lastSegment] = updatedItemData
          console.log(`Updated property "${lastSegment}" with:`, updatedItemData)
        }

        console.log('Merged data result:', updatedData)
        return updatedData
      },
      [data, selectedItem]
    )

    // Function to create a SelectedItem from a path segment
    const createSelectedItemFromPath = (path: string[], data: any): SelectedItem | null => {
      if (path.length === 0) return null

      // Helper function to navigate through nested loops
      const navigateToItem = (
        pathSegments: string[],
        currentData: any
      ): { data: any; path: string[] } | null => {
        if (pathSegments.length === 0) return null

        const [currentSegment, ...remainingSegments] = pathSegments

        if (currentSegment === 'loops') {
          // Navigate through loops
          if (remainingSegments.length === 0) return null

          const loopIndex = parseInt(remainingSegments[0])
          const loops = currentData.loops

          if (!loops || !loops[loopIndex]) return null

          const loop = loops[loopIndex]
          const remainingPath = remainingSegments.slice(1)

          if (remainingPath.length === 0) {
            return { data: loop, path: pathSegments }
          }

          // Continue navigation with the loop data
          return navigateToItem(remainingPath, loop)
        } else if (currentSegment === 'segments') {
          // Navigate to segments
          if (remainingSegments.length === 0) return null

          const segmentIndex = parseInt(remainingSegments[0])
          const segments = currentData.segments

          if (!segments || !segments[segmentIndex]) return null

          const segment = segments[segmentIndex]
          const remainingPath = remainingSegments.slice(1)

          if (remainingPath.length === 0) {
            return { data: segment, path: pathSegments }
          }

          // Continue navigation with the segment data
          return navigateToItem(remainingPath, segment)
        } else if (currentSegment === 'elements') {
          // Navigate to elements
          if (remainingSegments.length === 0) return null

          const elementIndex = parseInt(remainingSegments[0])
          const elements = currentData.elements

          if (!elements || !elements[elementIndex]) return null

          const element = elements[elementIndex]
          return { data: element, path: pathSegments }
        }

        return null
      }

      // Determine the type based on path structure
      let type: 'main-segment' | 'loop' | 'segment' | 'element' = 'main-segment'
      if (path.includes('loops')) {
        if (path.includes('elements')) {
          type = 'element'
        } else if (path.includes('segments')) {
          type = 'segment'
        } else {
          type = 'loop'
        }
      }

      // Navigate to the item
      const result = navigateToItem(path, data)
      if (!result) return null

      const { data: itemData, path: finalPath } = result

      // Generate unique ID based on the full path
      const pathString = finalPath.join('-')
      const id = `${type}-${pathString}`

      return {
        type,
        id,
        path: finalPath,
        data: itemData,
      }
    }

    // Function to handle breadcrumb item click
    const handleBreadcrumbClick = (clickedPath: string[]) => {
      if (onNavigateToItem) {
        const item = createSelectedItemFromPath(clickedPath, data)
        if (item) {
          onNavigateToItem(item)
          // Auto-toggle navigation pane when navigating
          if (onToggleNavigationPane && !navigationPaneVisible) {
            onToggleNavigationPane()
          }
        }
      }
    }

    // Function to generate meaningful breadcrumb nodes with icons and chips
    const getBreadcrumbNodes = (path: string[], selectedData: any) => {
      const nodes: React.ReactNode[] = []

      // Helper function to navigate through data structure
      const navigateToData = (pathSegments: string[], currentData: any): any => {
        if (pathSegments.length === 0) return currentData

        const [currentSegment, ...remainingSegments] = pathSegments

        if (currentSegment === 'loops') {
          if (remainingSegments.length === 0) return currentData
          const loopIndex = parseInt(remainingSegments[0])
          const loops = currentData.loops
          if (!loops || !loops[loopIndex]) return null
          return navigateToData(remainingSegments.slice(1), loops[loopIndex])
        } else if (currentSegment === 'segments') {
          if (remainingSegments.length === 0) return currentData
          const segmentIndex = parseInt(remainingSegments[0])
          const segments = currentData.segments
          if (!segments || !segments[segmentIndex]) return null
          return navigateToData(remainingSegments.slice(1), segments[segmentIndex])
        } else if (currentSegment === 'elements') {
          if (remainingSegments.length === 0) return currentData
          const elementIndex = parseInt(remainingSegments[0])
          const elements = currentData.elements
          if (!elements || !elements[elementIndex]) return null
          return navigateToData(remainingSegments.slice(1), elements[elementIndex])
        }

        return currentData
      }

      for (let i = 0; i < path.length; i++) {
        const segment = path[i]
        const currentPath = path.slice(0, i + 1)

        if (i === 0) {
          // First segment is a main segment key
          const title = MAIN_SEGMENT_TITLES[segment as keyof typeof MAIN_SEGMENT_TITLES] || segment
          const itemData = data[segment]

          nodes.push(
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                },
              }}
              onClick={() => handleBreadcrumbClick(currentPath)}
            >
              {getIcon('main-segment')}
              {itemData?.id && segment !== 'loops' && (
                <Chip
                  label={itemData.id}
                  size="small"
                  variant="outlined"
                  sx={{ height: '20px', fontSize: '0.75rem' }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {title}
              </Typography>
            </Box>
          )
        } else if (segment === 'loops') {
          nodes.push(
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                },
              }}
              onClick={() => handleBreadcrumbClick(currentPath)}
            >
              {getIcon('loop')}
              <Typography variant="body2" color="text.secondary">
                Loops
              </Typography>
            </Box>
          )
        } else if (segment === 'segments') {
          nodes.push(
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                },
              }}
              onClick={() => handleBreadcrumbClick(currentPath)}
            >
              {getIcon('segment')}
              <Typography variant="body2" color="text.secondary">
                Segments
              </Typography>
            </Box>
          )
        } else if (segment === 'elements') {
          nodes.push(
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                },
              }}
              onClick={() => handleBreadcrumbClick(currentPath)}
            >
              {getIcon('element')}
              <Typography variant="body2" color="text.secondary">
                Elements
              </Typography>
            </Box>
          )
        } else if (!isNaN(parseInt(segment))) {
          // This is an index - try to get the name from the data
          const index = parseInt(segment)
          let itemName = `Item ${index + 1}`
          let itemData: any = null
          let itemType = 'default'

          // Navigate to the actual data at this path
          itemData = navigateToData(currentPath, data)

          if (itemData) {
            // Determine the type based on the path structure
            if (currentPath.includes('loops') && !currentPath.includes('segments')) {
              itemType = 'loop'
              itemName = itemData.name || itemData.id || `Loop ${index + 1}`
            } else if (currentPath.includes('segments') && !currentPath.includes('elements')) {
              itemType = 'segment'
              itemName = itemData.name || itemData.id || `Segment ${index + 1}`
            } else if (currentPath.includes('elements')) {
              itemType = 'element'
              itemName = itemData.name || itemData.id || `Element ${index + 1}`
            }
          }

          nodes.push(
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                },
              }}
              onClick={() => handleBreadcrumbClick(currentPath)}
            >
              {getIcon(itemType)}
              {itemData?.id && (
                <Chip
                  label={itemData.id}
                  size="small"
                  variant="outlined"
                  sx={{ height: '20px', fontSize: '0.75rem' }}
                />
              )}
              {itemType === 'element' && itemData?.position && (
                <Chip
                  label={itemData.position}
                  size="small"
                  variant="outlined"
                  sx={{ height: '20px', fontSize: '0.75rem' }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {itemName}
              </Typography>
            </Box>
          )
        } else {
          // Fallback to the original segment
          nodes.push(
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                },
              }}
              onClick={() => handleBreadcrumbClick(currentPath)}
            >
              {getIcon('default')}
              <Typography variant="body2" color="text.secondary">
                {segment}
              </Typography>
            </Box>
          )
        }
      }

      return nodes
    }

    // Helper function to find the last loop index in a nested structure
    const findLastLoopIndex = (path: string[]): number => {
      let lastLoopIndex = -1
      for (let i = path.length - 1; i >= 0; i--) {
        if (path[i] === 'loops') {
          lastLoopIndex = parseInt(path[i + 1])
          break
        }
      }
      return lastLoopIndex
    }

    const renderInspector = () => {
      if (!selectedItem) {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
            }}
          >
            <Typography variant="h6">Select an item to view details</Typography>
          </Box>
        )
      }

      const lastLoopIndex = findLastLoopIndex(selectedItem.path)

      switch (selectedItem.type) {
        case 'main-segment':
          return (
            <MainSegmentInspector
              segment={selectedItem.data}
              segmentKey={selectedItem.id}
              onChange={updatedSegment => onChange(mergeUpdatedData(updatedSegment))}
              onNavigateToElement={(element, elementIndex) => {
                if (onNavigateToItem) {
                  const elementItem: SelectedItem = {
                    type: 'element',
                    id: `element-main-${element.id || elementIndex}`,
                    path: [selectedItem.path[0], 'elements', elementIndex.toString()],
                    data: element,
                  }
                  onNavigateToItem(elementItem)
                }
              }}
              readonly={readonly}
            />
          )
        case 'loop':
          return (
            <LoopInspector
              loop={selectedItem.data}
              loopIndex={parseInt(selectedItem.path[1])}
              onChange={updatedLoop => onChange(mergeUpdatedData(updatedLoop))}
              onNavigateToSegment={(segment, segmentIndex) => {
                if (onNavigateToItem) {
                  const segmentItem: SelectedItem = {
                    type: 'segment',
                    id: `segment-${segment.id || segmentIndex}`,
                    path: [...selectedItem.path.slice(0, 2), 'segments', segmentIndex.toString()],
                    data: segment,
                  }
                  onNavigateToItem(segmentItem)
                }
              }}
              readonly={readonly}
            />
          )
        case 'segment':
          // Determine if this segment is from a main segment or a loop
          const isFromMainSegment =
            selectedItem.path[0] !== 'loops' && selectedItem.path.includes('segments')
          const isFromLoop =
            selectedItem.path[0] === 'loops' && selectedItem.path.includes('segments')

          if (isFromMainSegment) {
            // Segment from main segment
            return (
              <SegmentInspector
                segment={selectedItem.data}
                segmentIndex={parseInt(selectedItem.path[2])}
                loopIndex={-1} // -1 indicates main segment
                onChange={updatedSegment => onChange(mergeUpdatedData(updatedSegment))}
                onNavigateToElement={(element, elementIndex) => {
                  if (onNavigateToItem) {
                    const elementItem: SelectedItem = {
                      type: 'element',
                      id: `element-main-${parseInt(selectedItem.path[2])}-${
                        element.id || elementIndex
                      }`,
                      path: [...selectedItem.path, 'elements', elementIndex.toString()],
                      data: element,
                    }
                    onNavigateToItem(elementItem)
                  }
                }}
                readonly={readonly}
              />
            )
          } else if (isFromLoop) {
            // Segment from loop
            return (
              <SegmentInspector
                segment={selectedItem.data}
                segmentIndex={parseInt(selectedItem.path[3])}
                loopIndex={lastLoopIndex}
                onChange={updatedSegment => onChange(mergeUpdatedData(updatedSegment))}
                onNavigateToElement={(element, elementIndex) => {
                  if (onNavigateToItem) {
                    const elementItem: SelectedItem = {
                      type: 'element',
                      id: `element-${lastLoopIndex}-${parseInt(selectedItem.path[3])}-${
                        element.id || elementIndex
                      }`,
                      path: [...selectedItem.path, 'elements', elementIndex.toString()],
                      data: element,
                    }
                    onNavigateToItem(elementItem)
                  }
                }}
                readonly={readonly}
              />
            )
          } else {
            return <Alert severity="warning">Invalid segment path structure</Alert>
          }
        case 'element':
          // Determine if this element is from a main segment or a loop
          const isElementFromMainSegment =
            selectedItem.path[0] !== 'loops' && selectedItem.path.includes('elements')
          const isElementFromLoop =
            selectedItem.path[0] === 'loops' && selectedItem.path.includes('elements')

          if (isElementFromMainSegment) {
            // Element from main segment
            return (
              <ElementInspector
                element={selectedItem.data}
                elementIndex={parseInt(selectedItem.path[2])}
                segmentIndex={0} // Main segment is always at index 0
                loopIndex={-1} // -1 indicates main segment
                onChange={updatedElement => onChange(mergeUpdatedData(updatedElement))}
                readonly={readonly}
              />
            )
          } else if (isElementFromLoop) {
            // Element from loop
            return (
              <ElementInspector
                element={selectedItem.data}
                elementIndex={parseInt(selectedItem.path[5])}
                segmentIndex={parseInt(selectedItem.path[3])}
                loopIndex={lastLoopIndex}
                onChange={updatedElement => onChange(mergeUpdatedData(updatedElement))}
                readonly={readonly}
              />
            )
          } else {
            return <Alert severity="warning">Invalid element path structure</Alert>
          }
        default:
          return <Alert severity="warning">Unknown item type: {selectedItem.type}</Alert>
      }
    }

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Stack spacing={1} padding={2} sx={{ flexShrink: 0 }}>
          <Typography variant="h6" component="h2" fontWeight={'bold'}>
            Inspector
          </Typography>
          <Divider />
          {selectedItem && (
            <Stack spacing={1}>
              <Breadcrumbs separator={<ArrowRight />}>
                {getBreadcrumbNodes(selectedItem.path, data)}
              </Breadcrumbs>
            </Stack>
          )}
        </Stack>

        {/* Inspector Content with scroll overflow */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0, // Important for flex child to shrink
          }}
        >
          {renderInspector()}
        </Box>
      </Box>
    )
  }
)

InspectorPane.displayName = 'InspectorPane'

export default InspectorPane
